<template>
  <div class="flex flex-col max-h-[80vh]">
    <div class="overflow-y-auto px-4 lg:px-8 py-4 lg:py-6">
      <div class="mb-6">
        <label class="block text-sm font-medium text-surface-700 mb-2">
          Export comptable <span class="text-red-500">*</span>
        </label>
        <Select
          v-model="selectedExport"
          :options="accountancyexports"
          show-clear
          option-label="name"
          option-value="value"
          placeholder="-- Sélectionner un type d'export --"
          class="w-full"
        />
      </div>

      <Transition
        enter-active-class="transition-all duration-300 ease-in-out"
        enter-from-class="opacity-0 -translate-y-2"
        leave-active-class="transition-all duration-300 ease-in-out"
        leave-to-class="opacity-0 translate-y-2"
      >
        <div
          v-if="selectedExport !== null"
          :class="[
            'grid gap-4 lg:gap-6',
            hasFilters && hasOptions ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1',
          ]"
        >
          <div
            v-if="hasFilters"
            class="space-y-4"
          >
            <Fieldset
              legend="Filtres principaux"
              :toggleable="false"
            >
              <div
                v-if="selectedExport === 'transfertmerchant'"
                class="space-y-4 mb-4"
              >
                <div>
                  <span class="block text-sm font-medium text-surface-700 mb-2">Filtrer les factures par date</span>
                  <Select
                    v-model="transferDateFilterMode"
                    :options="transferDateFilterModes"
                    option-label="name"
                    option-value="value"
                    class="w-full"
                  />
                  <small class="block text-surface-500 mt-1">
                    Les dates filtrent les factures affichées, pas les entreprises listées.
                  </small>
                </div>

                <template v-if="transferDateFilterMode === 'billing_period'">
                  <div>
                    <span class="block text-sm font-medium text-surface-700 mb-2">Date début période de facturation</span>
                    <DatePicker
                      v-model="billingPeriodStartDate"
                      show-icon
                      :show-on-focus="false"
                      show-button-bar
                      class="w-full"
                    />
                  </div>

                  <div>
                    <span class="block text-sm font-medium text-surface-700 mb-2">Date fin période de facturation</span>
                    <DatePicker
                      v-model="billingPeriodEndDate"
                      show-icon
                      :show-on-focus="false"
                      show-button-bar
                      class="w-full"
                    />
                  </div>
                </template>
              </div>

              <!-- Date début -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="shouldShow('fromList')">
                  <span class="block text-sm font-medium text-surface-700 mb-2">{{ startDateLabel }}</span>
                  <DatePicker
                    v-model="startdate"
                    show-icon
                    :show-on-focus="false"
                    show-button-bar
                    class="w-full"
                  />
                  <Message
                    v-if="errorMessageStartDate"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageStartDate }}
                  </Message>
                </div>
              </Transition>

              <!-- Date fin -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="shouldShow('toList')">
                  <span class="block text-sm font-medium text-surface-700 mb-2">{{ endDateLabel }}</span>
                  <DatePicker
                    v-model="enddate"
                    show-icon
                    :show-on-focus="false"
                    show-button-bar
                    class="w-full"
                  />
                  <Message
                    v-if="errorMessageEndDate"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageEndDate }}
                  </Message>
                </div>
              </Transition>

              <!-- Ville (modelSearch) -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="shouldShow('cityList')">
                  <span class="block text-sm font-medium text-surface-700 mb-2">Ville</span>
                  <ModelSearch
                    v-model="selectedCity"
                    api-url="/api/cities"
                    :no-pagination="true"
                    @selection="onCitySelected"
                  />
                  <Message
                    v-if="errorMessageCity"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageCity }}
                  </Message>
                </div>
              </Transition>

              <!-- Toutes les villes -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="shouldShow('allCitiesList')">
                  <span class="block text-sm font-medium text-surface-700 mb-2">Ville</span>
                  <Select
                    v-model="selectedCity"
                    :options="allCities"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder="-- Sélectionner une ville --"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                  <Message
                    v-if="errorMessageCity"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageCity }}
                  </Message>
                </div>
              </Transition>

              <div
                v-if="selectedExport === 'transfertmerchant'"
                class="space-y-4"
              >
                <div>
                  <span class="block text-sm font-medium text-surface-700 mb-2">Entité de facturation</span>
                  <Select
                    v-model="selectedInvoiceSetting"
                    :options="invoiceSettings"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder="-- Sélectionner une entité --"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>

                <div>
                  <span class="block text-sm font-medium text-surface-700 mb-2">Ville</span>
                  <Select
                    v-model="selectedTransferCity"
                    :options="transferCities"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder="-- Sélectionner une ville --"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>

                <div>
                  <span class="block text-sm font-medium text-surface-700 mb-2">Marché</span>
                  <Select
                    v-model="selectedTransferMarket"
                    :options="filteredTransferMarkets"
                    show-clear
                    filter
                    option-label="name"
                    option-value="value"
                    placeholder="-- Sélectionner un marché --"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </div>

              <!-- Périodicité -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="shouldShow('periodicityList')">
                  <span class="block text-sm font-medium text-surface-700 mb-2">Périodicité</span>
                  <Select
                    v-model="selectedPeriodicity"
                    :options="periodicities"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder="-- Sélectionner la périodicité --"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                  <Message
                    v-if="errorMessagePeriodicity"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessagePeriodicity }}
                  </Message>
                </div>
              </Transition>

              <!-- Code comptable -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="shouldShow('accountingCodeList')">
                  <span class="block text-sm font-medium text-surface-700 mb-2">Code comptable du produit</span>
                  <Select
                    v-model="selectedAccountingCode"
                    :options="accountingCodes"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder="-- Sélectionner le code comptable --"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                  <Message
                    v-if="errorMessageAccountingCode"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageAccountingCode }}
                  </Message>
                </div>
              </Transition>
            </Fieldset>
          </div>

          <div
            v-if="hasOptions"
            class="space-y-4"
          >
            <Fieldset
              legend="Options d'export"
              :toggleable="false"
            >
              <!-- Filtrer par date de paiement -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div
                  v-if="shouldShow('paymentDateFilterList')"
                  class="flex items-center gap-2"
                >
                  <Checkbox
                    v-model="paymentDateFilterValue"
                    input-id="paymentDateFilterValue"
                    name="paymentDateFilterValue"
                    binary
                  />
                  <label
                    for="paymentDateFilterValue"
                    class="text-sm cursor-pointer"
                  >
                    Filtrer par date de paiement
                  </label>
                </div>
              </Transition>

              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div
                  v-if="selectedExport === 'transfertmerchant'"
                  class="flex items-start gap-2"
                >
                  <Checkbox
                    v-model="transferOnlyWithUnpaidInvoices"
                    input-id="transferOnlyWithUnpaidInvoices"
                    name="transferOnlyWithUnpaidInvoices"
                    binary
                  />
                  <label
                    for="transferOnlyWithUnpaidInvoices"
                    class="text-sm cursor-pointer leading-5"
                  >
                    Afficher uniquement les entreprises avec factures impayées
                  </label>
                </div>
              </Transition>
            </Fieldset>
          </div>
        </div>
      </Transition>
    </div>

    <Toast />

    <div
      class="sticky bottom-0 flex justify-end gap-3 px-4 lg:px-8 pt-4 pb-6 border-t border-surface-200 bg-surface-50/95 backdrop-blur-sm"
    >
      <Button
        label="Annuler"
        text
        @click="closeDialog"
      />
      <Button
        v-if="selectedExport && shouldShow('exportButtonCSV')"
        label="Export CSV"
        severity="info"
        icon="pi pi-download"
        :loading="isExportingCSV"
        :disabled="isExportingCSV"
        @click="fetchCSVFile"
      />
      <Button
        v-if="selectedExport && shouldShow('exportButton')"
        label="Export Excel"
        severity="info"
        icon="pi pi-download"
        :loading="!isDataLoaded || isExportingExcel"
        :disabled="isExportingExcel"
        @click="fetchExcelFile"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject, watch, type Ref } from "vue";
import { format } from "date-fns";
import Button from "primevue/button";
import Select from "primevue/select";
import DatePicker from "primevue/datepicker";
import Checkbox from "primevue/checkbox";
import Fieldset from "primevue/fieldset";
import Message from "primevue/message";
import Toast from "primevue/toast";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";

import ModelSearch from "~/components/atoms/model/search";
import type { City } from "~/types/City";
import { downloadFileFromBlob } from "~/utils/download";
import { useToast } from "primevue/usetoast";

const dialogRef = inject<Ref<DynamicDialogInstance>>("dialogRef");

const closeDialog = () => {
  dialogRef?.value?.close();
};

const toast = useToast();
const $apiFetch = useSanctumClient();

const isExportingCSV = ref(false);
const isExportingExcel = ref(false);
const isDataLoaded = ref(false);
const stopErrorExport = ref(false);

const selectedExport = ref<string | null>(null);
const selectedCity = ref<string | number>("");
const selectedPeriodicity = ref<number | null>(null);
const selectedAccountingCode = ref<string | number>("");
const selectedInvoiceSetting = ref<number | null>(null);
const selectedTransferCity = ref<number | null>(null);
const selectedTransferMarket = ref<number | null>(null);
const city = ref<City | null>(null);

const startdate = ref<Date | null>(new Date());
const enddate = ref<Date | null>(new Date());
const billingPeriodStartDate = ref<Date | null>(null);
const billingPeriodEndDate = ref<Date | null>(null);
const transferDateFilterMode = ref<"invoice_issue" | "billing_period">("invoice_issue");

const paymentDateFilterValue = ref<boolean>(false);
const transferOnlyWithUnpaidInvoices = ref<boolean>(false);
const accountingCodes = ref<{ name: string; value: number }[]>([]);
const allCities = ref<{ name: string; value: number }[]>([]);
const invoiceSettings = ref<{ name: string; value: number }[]>([]);
const allMarkets = ref<{ name: string; value: number; cityId: number | null }[]>([]);

const transferDateFilterModes = [
  { name: "Date d'émission des factures", value: "invoice_issue" },
  { name: "Période de facturation", value: "billing_period" },
];

// Messages d'erreur
const errorMessageStartDate = ref("");
const errorMessageEndDate = ref("");
const errorMessageCity = ref("");
const errorMessagePeriodicity = ref("");
const errorMessageAccountingCode = ref("");

const startdateformat = computed(() => startdate.value ? format(startdate.value, "yyyy-MM-dd") : null);
const startdateformatfr = computed(() => startdate.value ? format(startdate.value, "dd-MM-yyyy") : null);
const enddateformat = computed(() => enddate.value ? format(enddate.value, "yyyy-MM-dd") : null);
const enddateformatfr = computed(() => enddate.value ? format(enddate.value, "dd-MM-yyyy") : null);
const transferCities = computed(() => allCities.value.filter(option => option.value !== -2));
const filteredTransferMarkets = computed(() => {
  if (!selectedTransferCity.value) return allMarkets.value;
  return allMarkets.value.filter(market => market.cityId === selectedTransferCity.value);
});

watch(selectedTransferCity, () => {
  if (!filteredTransferMarkets.value.some(market => market.value === selectedTransferMarket.value)) {
    selectedTransferMarket.value = null;
  }
});

watch(transferDateFilterMode, (mode) => {
  if (mode === "invoice_issue") {
    billingPeriodStartDate.value = null;
    billingPeriodEndDate.value = null;
    return;
  }

  startdate.value = null;
  enddate.value = null;
  errorMessageStartDate.value = "";
  errorMessageEndDate.value = "";
});

watch(selectedExport, (type, previousType) => {
  if (type === "transfertmerchant") {
    startdate.value = null;
    enddate.value = null;
    billingPeriodStartDate.value = null;
    billingPeriodEndDate.value = null;
    transferDateFilterMode.value = "invoice_issue";
    transferOnlyWithUnpaidInvoices.value = false;
    errorMessageStartDate.value = "";
    errorMessageEndDate.value = "";
    return;
  }

  if (previousType === "transfertmerchant") {
    startdate.value ??= new Date();
    enddate.value ??= new Date();
  }
});

const accountancyexports = [
  { name: "Liste des placiers (530/531)", value: "placier" },
  { name: "Liste des commerçants (comptes Clients)", value: "commerçant" },
  { name: "Modification de commerçants (comptes Clients)", value: "commercantchanged" },
  { name: "Journal de ventes", value: "ventes" },
  { name: "Journal de banque (ESP / CHQ)", value: "banqueESPCHQ" },
  { name: "Journal de banque (CB / VRT / PRLV)", value: "banqueCBVRT" },
  { name: "Journal de caisse", value: "caisse" },
  { name: "Reporting CA", value: "chiffredaffaires" },
  { name: "Reporting CA (avec articles)", value: "chiffredaffairesarticles" },
  { name: "Reporting CA (avec articles) seulement factures payés", value: "chiffredaffairesarticlepayes" },
  { name: "Reporting CA (avec articles) date d'abonnement réel", value: "chiffredaffairesarticlereel" },
  { name: "Factures erronées", value: "factureserreurs" },
  { name: "Factures et articles en avoir", value: "avoirs" },
  { name: "Export Article", value: "taxeexport" },
  { name: "Prélévements des commerçants", value: "transfertmerchant" },
  { name: "Soldes des commerçants", value: "soldesbalances" },
];

const periodicities = [
  { name: "Sans", value: 1 },
  { name: "Semaine", value: 2 },
  { name: "Double semaine", value: 3 },
  { name: "Mois", value: 4 },
  { name: "Trimestre", value: 5 },
  { name: "Année", value: 6 },
];

const accountancyExportNamReverse = {
  placier: "Liste_des_placiers",
  commerçant: "Liste_des_commerçants",
  commercantchanged: "Modification_de_commerçants",
  ventes: "Journal_de_vente",
  banqueESPCHQ: "Journal_de_banque_(ESP/CHQ)",
  banqueCBVRT: "Journal_de_banque_(CB/VRT/PRLV)",
  caisse: "Journal_de_caisse",
  chiffredaffaires: "Reporting_CA",
  chiffredaffairesarticles: "Reporting_CA_(avec_articles)",
  chiffredaffairesarticlepayes: "Reporting_CA_(avec_articles)_seulement_factures_payés",
  chiffredaffairesarticlereel: "Reporting_CA_(avec_articles)_seulement_factures_payés_date_abonnement",
  factureserreurs: "Factures_erronées",
  avoirs: "Factures_et_articles_en_avoir",
  taxeexport: "Export_Produit",
  transfertmerchant: "Prélévement_Commerçant",
  soldesbalances: "Soldes_des_commercants",
};

const periodicityTab: Record<number, string> = {
  1: "",
  2: "Semaine",
  3: "Double_Semaine",
  4: "Mois",
  5: "Trimestre",
  6: "Année",
};

const initiallist = {
  placier: { fromList: false, toList: false, cityList: false, periodicityList: false, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: true, allCitiesList: false },
  commerçant: { fromList: false, toList: false, cityList: false, periodicityList: false, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: true, allCitiesList: false },
  commercantchanged: { fromList: true, toList: true, cityList: false, periodicityList: false, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: true, allCitiesList: false },
  ventes: { fromList: true, toList: true, cityList: false, periodicityList: false, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: true, allCitiesList: false },
  banqueESPCHQ: { fromList: true, toList: true, cityList: false, periodicityList: false, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: true, allCitiesList: false },
  banqueCBVRT: { fromList: true, toList: true, cityList: false, periodicityList: false, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: true, allCitiesList: false },
  caisse: { fromList: true, toList: true, cityList: false, periodicityList: false, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: true, allCitiesList: false },
  chiffredaffaires: { fromList: true, toList: true, cityList: false, periodicityList: false, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: false, allCitiesList: true },
  chiffredaffairesarticles: { fromList: true, toList: true, cityList: true, periodicityList: false, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: false, allCitiesList: false },
  chiffredaffairesarticlepayes: { fromList: true, toList: true, cityList: false, periodicityList: true, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: false, allCitiesList: true },
  chiffredaffairesarticlereel: { fromList: true, toList: true, cityList: false, periodicityList: true, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: false, allCitiesList: true },
  factureserreurs: { fromList: true, toList: true, cityList: false, periodicityList: false, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: false, allCitiesList: false },
  avoirs: { fromList: true, toList: true, cityList: false, periodicityList: false, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: false, allCitiesList: false },
  taxeexport: { fromList: true, toList: true, cityList: true, periodicityList: false, accountingCodeList: true, paymentDateFilterList: true, exportButton: true, exportButtonCSV: false, allCitiesList: false },
  transfertmerchant: { fromList: true, toList: true, cityList: false, periodicityList: false, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: true, allCitiesList: false },
  soldesbalances: { fromList: true, toList: false, cityList: false, periodicityList: false, accountingCodeList: false, paymentDateFilterList: false, exportButton: true, exportButtonCSV: true, allCitiesList: false },
} as const;

const shouldShow = (key: keyof typeof initiallist["placier"]) => {
  if (!selectedExport.value) return false;
  if (
    selectedExport.value === "transfertmerchant"
    && (key === "fromList" || key === "toList")
  ) {
    return transferDateFilterMode.value === "invoice_issue";
  }

  return initiallist[selectedExport.value as keyof typeof initiallist]?.[key] ?? false;
};

const hasFilters = computed(() => {
  if (!selectedExport.value) return false;
  return (
    shouldShow("fromList")
    || shouldShow("toList")
    || shouldShow("cityList")
    || shouldShow("allCitiesList")
    || shouldShow("periodicityList")
    || shouldShow("accountingCodeList")
    || selectedExport.value === "transfertmerchant"
  );
});

const hasOptions = computed(() => {
  if (!selectedExport.value) return false;
  return shouldShow("paymentDateFilterList") || selectedExport.value === "transfertmerchant";
});

const onCitySelected = (_: unknown, obj: City) => {
  city.value = obj;
};

const buildExportQuery = (formatType?: "csv") => {
  const query: Record<string, string | number | boolean | null> = {
    type: selectedExport.value,
    cityId: selectedCity.value,
    periodicityId: selectedPeriodicity.value,
    accountingCodeId: selectedAccountingCode.value,
    paymentDateFilterValue: paymentDateFilterValue.value,
  };

  if (selectedExport.value && shouldShow("fromList") && startdateformat.value) {
    query.from = startdateformat.value;
  }

  if (selectedExport.value && shouldShow("toList") && enddateformat.value) {
    query.to = enddateformat.value;
  }

  if (selectedExport.value === "soldesbalances") {
    query.stopped_at = startdateformat.value;
  }

  if (selectedExport.value === "transfertmerchant") {
    query.only_with_unpaid_invoices = transferOnlyWithUnpaidInvoices.value;

    if (transferDateFilterMode.value === "billing_period" && billingPeriodStartDate.value) {
      query.billing_period_start_date = format(billingPeriodStartDate.value, "yyyy-MM-dd");
    }
    if (transferDateFilterMode.value === "billing_period" && billingPeriodEndDate.value) {
      query.billing_period_end_date = format(billingPeriodEndDate.value, "yyyy-MM-dd");
    }
    if (selectedInvoiceSetting.value) {
      query.invoice_setting_id = selectedInvoiceSetting.value;
    }
    if (selectedTransferCity.value) {
      query.city_id = selectedTransferCity.value;
    }
    if (selectedTransferMarket.value) {
      query.market_id = selectedTransferMarket.value;
    }
  }

  if (formatType === "csv") {
    query.format = "csv";
  }

  return query;
};

const buildExportFilename = () => {
  if (!selectedExport.value) return "export";

  if (selectedExport.value === "soldesbalances") {
    return (
      accountancyExportNamReverse[selectedExport.value as keyof typeof accountancyExportNamReverse]
      + `_${startdateformatfr.value}`
    );
  }

  let cityLabel
    = city.value?.name
      && (initiallist[selectedExport.value as keyof typeof initiallist]?.cityList === true
        || initiallist[selectedExport.value as keyof typeof initiallist]?.allCitiesList === true)
      ? `_${city.value.name}`
      : "";

  if (
    selectedCity.value === -2
    && (initiallist[selectedExport.value as keyof typeof initiallist]?.cityList === true
      || initiallist[selectedExport.value as keyof typeof initiallist]?.allCitiesList === true)
  ) {
    cityLabel = "_Toutes_les_villes";
  }

  const periodicityLabel
    = selectedPeriodicity.value
      && initiallist[selectedExport.value as keyof typeof initiallist]?.periodicityList === true
      ? `_${periodicityTab[selectedPeriodicity.value]}`
      : "";

  const startDateLabelValue
    = initiallist[selectedExport.value as keyof typeof initiallist]?.fromList === true && startdateformatfr.value
      ? `_${startdateformatfr.value}`
      : "";

  const endDateLabelValue
    = initiallist[selectedExport.value as keyof typeof initiallist]?.toList === true && enddateformatfr.value
      ? `_${enddateformatfr.value}`
      : "";

  return (
    accountancyExportNamReverse[selectedExport.value as keyof typeof accountancyExportNamReverse]
    + cityLabel
    + periodicityLabel
    + startDateLabelValue
    + endDateLabelValue
  );
};

const validateExportForm = () => {
  stopErrorExport.value = false;

  errorMessageStartDate.value = "";
  errorMessageEndDate.value = "";
  errorMessageCity.value = "";
  errorMessagePeriodicity.value = "";
  errorMessageAccountingCode.value = "";

  if (!selectedExport.value) {
    stopErrorExport.value = true;
    return false;
  }

  if (
    selectedExport.value !== "transfertmerchant"
    && initiallist[selectedExport.value as keyof typeof initiallist]?.fromList === true
  ) {
    if (!startdate.value) {
      errorMessageStartDate.value
        = selectedExport.value === "soldesbalances"
          ? "Veuillez renseigner la date d’arrêté."
          : "Veuillez renseigner la date de début.";
      stopErrorExport.value = true;
    }
  }

  if (
    selectedExport.value !== "transfertmerchant"
    && initiallist[selectedExport.value as keyof typeof initiallist]?.toList === true
  ) {
    if (!enddate.value) {
      errorMessageEndDate.value = "Veuillez renseigner la date de fin.";
      stopErrorExport.value = true;
    }
  }

  if (
    initiallist[selectedExport.value as keyof typeof initiallist]?.cityList === true
    || initiallist[selectedExport.value as keyof typeof initiallist]?.allCitiesList === true
  ) {
    if (!selectedCity.value) {
      errorMessageCity.value = "Veuillez sélectionner une ville.";
      stopErrorExport.value = true;
    }
  }

  if (initiallist[selectedExport.value as keyof typeof initiallist]?.periodicityList === true) {
    if (!selectedPeriodicity.value) {
      errorMessagePeriodicity.value = "Veuillez sélectionner une périodicité.";
      stopErrorExport.value = true;
    }
  }

  if (initiallist[selectedExport.value as keyof typeof initiallist]?.accountingCodeList === true) {
    if (!selectedAccountingCode.value) {
      errorMessageAccountingCode.value = "Veuillez sélectionner un code comptable.";
      stopErrorExport.value = true;
    }
  }

  return !stopErrorExport.value;
};

const fetchCSVFile = async () => {
  isExportingCSV.value = true;

  if (!validateExportForm()) {
    isExportingCSV.value = false;
    return;
  }

  try {
    const blob = await $apiFetch("/api/exportaccountancy", {
      method: "GET",
      responseType: "blob",
      query: buildExportQuery("csv"),
    });

    downloadFileFromBlob(blob, buildExportFilename());
    toast.add({ severity: "success", summary: "Export réussi", life: 8000 });
  }
  catch (error) {
    console.error("Erreur lors de l'export CSV :", error);
    toast.add({
      severity: "error",
      summary: "Erreur export",
      detail: "Impossible de générer le fichier",
      life: 8000,
    });
  }
  finally {
    isExportingCSV.value = false;
  }
};

const fetchExcelFile = async () => {
  isExportingExcel.value = true;

  if (!validateExportForm()) {
    isExportingExcel.value = false;
    return;
  }

  try {
    const blob = await $apiFetch("/api/exportaccountancy", {
      method: "GET",
      responseType: "blob",
      query: buildExportQuery(),
    });

    downloadFileFromBlob(blob, buildExportFilename());
    toast.add({ severity: "success", summary: "Export réussi", life: 3000 });
  }
  catch (error) {
    console.error("Erreur lors de l'export Excel :", error);
    toast.add({
      severity: "error",
      summary: "Erreur export",
      detail: "Impossible de générer le fichier",
      life: 4000,
    });
  }
  finally {
    isExportingExcel.value = false;
  }
};

onMounted(async () => {
  try {
    const response = await $apiFetch("/api/accountingCodes", {
      query: { no_pagination: true },
    });

    accountingCodes.value = response.data
      .filter((item: { code_type: number | string }) => Number(item.code_type) === 1)
      .map((item: { id: number; name: string; description: string }) => ({
        name: `${item.name} - ${item.description}`,
        value: item.id,
      }));

    const response2 = await $apiFetch("/api/cities", {
      query: { no_pagination: true },
    });

    allCities.value = response2.data
      .filter((item: { enable: boolean | number }) => Number(item.enable) === 1)
      .map((item: { id: number; name: string }) => ({
        name: item.name,
        value: item.id,
      }));

    allCities.value = [{ name: "Toutes les villes", value: -2 }, ...allCities.value];

    try {
      const invoiceSettingsResponse = await $apiFetch("/api/invoiceSetting", {
        query: { no_pagination: true },
      });

      invoiceSettings.value = invoiceSettingsResponse.data.map((item: { id: number; entity_name?: string | null; company_name?: string | null; code?: string | null }) => ({
        name: item.entity_name || item.company_name || item.code || `Entité #${item.id}`,
        value: item.id,
      }));
    }
    catch (error) {
      console.error("Erreur lors du chargement des entités de facturation", error);
    }

    try {
      const marketsResponse = await $apiFetch("/api/markets", {
        query: { no_pagination: true },
      });

      allMarkets.value = marketsResponse.data
        .filter((item: { enable: boolean | number }) => Number(item.enable) === 1)
        .map((item: { id: number; name: string; city_id?: number | null }) => ({
          name: item.name,
          value: item.id,
          cityId: item.city_id ?? null,
        }));
    }
    catch (error) {
      console.error("Erreur lors du chargement des marchés", error);
    }

    isDataLoaded.value = true;
  }
  catch (error) {
    console.error("Erreur lors du chargement des villes", error);
  }
});

const startDateLabel = computed(() => {
  const type = selectedExport.value;

  if (!type) return "Date de début";

  if (type === "taxeexport") {
    return paymentDateFilterValue.value
      ? "Début date de paiement des factures"
      : "Début date d'émission des factures";
  }

  const labels: Record<string, string> = {
    placier: "",
    commerçant: "",
    commercantchanged: "À partir de",
    ventes: "Date début",
    banqueESPCHQ: "Date début",
    banqueCBVRT: "Date début de création du paiement",
    caisse: "Date début",
    chiffredaffaires: "Début date d'émission des factures",
    chiffredaffairesarticles: "Début date d'émission des factures",
    chiffredaffairesarticlepayes: "Début date de paiement des factures",
    chiffredaffairesarticlereel: "Début date de période de facturation des factures",
    factureserreurs: "Début date d'émission des factures",
    avoirs: "Début date d'émission des factures",
    transfertmerchant: "Début date d'émission des factures (facultatif)",
    soldesbalances: "Date d’arrêté",
  };

  return labels[type] ?? "Date de début";
});

const endDateLabel = computed(() => {
  const type = selectedExport.value;

  if (!type) return "Date de fin";

  if (type === "taxeexport") {
    return paymentDateFilterValue.value
      ? "Fin date de paiement des factures"
      : "Fin date d'émission des factures";
  }

  const labelsend: Record<string, string> = {
    placier: "",
    commerçant: "",
    commercantchanged: "Jusqu'à",
    ventes: "Date fin",
    banqueESPCHQ: "Date fin",
    banqueCBVRT: "Date fin de création du paiement",
    caisse: "Date fin",
    chiffredaffaires: "Fin date d'émission des factures",
    chiffredaffairesarticles: "Fin date d'émission des factures",
    chiffredaffairesarticlepayes: "Fin date de paiement des factures",
    chiffredaffairesarticlereel: "Fin date de période de facturation des factures",
    factureserreurs: "Fin date d'émission des factures",
    avoirs: "Fin date d'émission des factures",
    transfertmerchant: "Fin date d'émission des factures (facultatif)",
  };

  return labelsend[type] ?? "Date de fin";
});

const _messageExplanation = computed(() => {
  const type = selectedExport.value;

  if (!type) return "Selectionnez un type d'export.";

  const labelsmesage: Record<string, string> = {
    placier: `Cet export contient la liste des placiers. 
. 2 lignes par placier (une pour chèque et une pour espèce)

. Colonnes à afficher :
. compte : combinaison de 530 pour espèce et 531 pour chèque puis l’ID unique du placier
. placier : nom et prénom du placier
. type de paiement : espèce ou chèque`,

    commerçant: `. Cet export contient la liste des commerçants non archivés.

. Colonnes à afficher :
. compte client : code comptable enregistré au niveau du commerçant
. commerçant : nom et prénom du commerçant
. téléphone (si disponible)
. e-mail (si disponible)`,

    commercantchanged: "Export des modifications de commerçants",

    ventes: `Cet export contient le détail des factures et avoirs créés dans la période de dates sélectionnées hors statut “abandonnée” et “brouillon”, ni de type “offre”.

Champs filtres obligatoires :
. Date de début
. Date de fin`,

    banqueESPCHQ: "Journal de banque pour les paiements en espèces et chèques.",
    banqueCBVRT: "Journal de banque pour les paiements en Carte bleu, virement et prélèvement.",
    caisse: "Journal de caisse",
    chiffredaffaires: "Export chiffre d'affaires",
    chiffredaffairesarticles: "Export chiffre d'affaires avec articles",
    chiffredaffairesarticlepayes: "Export chiffre d'affaires avec articles pour les factures payées",
    chiffredaffairesarticlereel: "Export chiffre d'affaires avec articles trié par date de début d'abonnement réel",
    factureserreurs: "Export des factures erronées",
    avoirs: "Export des factures et articles en avoir",
    taxeexport: "Export Article",
    transfertmerchant: "Export prélevement commerçant",
    soldesbalances: `Cet export contient le solde de chaque balance commerçant à la date d'arrêté sélectionnée.

. Champs filtres obligatoires :
. Date d’arrêté

. Colonnes attendues :
. entreprise
. commerçant
. code comptable
. solde à la date d’arrêté
. date du dernier mouvement retenu`,
  };

  return labelsmesage[type] ?? "defaut test";
});
</script>

<style>
.button {
  padding: 10px 20px;
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 7px;
  cursor: pointer;
}

.button:hover {
  background-color: #0056b3;
}

:deep(.p-dialog) {
  position: fixed !important;
  top: 10px !important;
  right: 10px !important;
}

:deep(.p-tooltip) {
  background-color: rgba(0, 0, 0, 0.85);
  color: white !important;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.p-message-error {
  background-color: #f8d7da;
  border: 1px solid #f5c2c7;
  color: #842029;
  padding: 0.2rem 0.2rem;
  border-radius: 2px;
  display: flex;
  align-items: center;
  font-size: 0.95rem;
}

.p-message-error i {
  font-size: 1.1rem;
}
</style>
